import {
  ArrowsClockwiseIcon,
  BrowserIcon,
  CubeIcon,
  DeviceMobileIcon,
  type Icon,
  ShoppingBagOpenIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';

import { useContent } from '@/content/ContentContext';
import { BRAND_STATUSES, PROJECT_TYPES, type ProjectType } from '@/domain/brief/BriefOptions';
import type { BriefWizardController } from '@/features/brief/useBriefWizard';
import { ChoiceCard, ChoiceChip } from '@/ui/Form/Choice';

import styles from '../Brief.module.css';

const PROJECT_ICONS: Readonly<Record<ProjectType, Icon>> = {
  showcase: BrowserIcon,
  ecommerce: ShoppingBagOpenIcon,
  'mobile-app': DeviceMobileIcon,
  immersive: CubeIcon,
  redesign: ArrowsClockwiseIcon,
};

export function ProjectStep({ controller }: { readonly controller: BriefWizardController }) {
  const { brief } = useContent();
  const { wizard } = controller;
  const { projectType, brandStatus } = wizard.values;

  return (
    <fieldset className={styles.step}>
      <legend className="visually-hidden">{brief.steps.project.title}</legend>
      <h3 className={styles.question} tabIndex={-1} data-step-heading>
        {brief.steps.project.question}
      </h3>
      <div className={styles.cards}>
        {PROJECT_TYPES.map((type) => {
          const Glyph = PROJECT_ICONS[type];
          return (
            <ChoiceCard
              key={type}
              selected={projectType === type}
              onSelect={() => {
                controller.update('projectType', type);
              }}
              icon={<Glyph size={20} weight="light" />}
              title={brief.projectTypes[type].label}
              {...(brief.projectTypes[type].description
                ? { description: brief.projectTypes[type].description }
                : {})}
            />
          );
        })}
      </div>

      <p className={styles.subQuestion} id="brief-brand">
        {brief.brandQuestion} <span className={styles.optional}>({brief.optional})</span>
      </p>
      <div className={styles.chips} role="group" aria-labelledby="brief-brand">
        {BRAND_STATUSES.map((status) => (
          <ChoiceChip
            key={status}
            selected={brandStatus === status}
            onSelect={() => {
              controller.update('brandStatus', brandStatus === status ? null : status);
            }}
          >
            {brief.brandStatuses[status].label}
          </ChoiceChip>
        ))}
      </div>

      {wizard.notice === 'projectType' && (
        <p className={styles.notice} role="alert" data-step-notice>
          <WarningCircleIcon size={18} aria-hidden="true" />
          {brief.errors.projectType}
        </p>
      )}
    </fieldset>
  );
}
